<template>
  <div>
    <!-- ============================================================== -->
    <!-- Container fluid  -->
    <!-- ============================================================== -->
    <div class="container-fluid">
      <!-- ============================================================== -->
      <!-- Bread crumb and right sidebar toggle -->
      <!-- ============================================================== -->
      <div class="row page-titles">
        <div class="col-md-5 col-8 align-self-center">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link to="/home">Home</router-link>
            </li>
            <li class="breadcrumb-item active">利用者一覧</li>
          </ol>
        </div>
      </div>
      <!-- ============================================================== -->
      <!-- End Bread crumb and right sidebar toggle -->
      <!-- ============================================================== -->
      <div class="row">
        <div class="col-12">
          <!-- Column -->
          <div class="card">
            <div class="card-body">
              <div class="dataTables_wrapper dt-bootstrap no-footer">
                <div class="row">
                  <div class="col-4">
                    <div class="p-xl-3" :class="{'has-danger': errorOfficeId ? true : false }">
                      <label class="control-label">事業所</label>
                      <select-office id="select-office" v-model="targetOfficeId"/>
                      <div class="text-danger">{{errorOfficeId}}</div>
                    </div>
                  </div>
                  <div class="col-3">
                    <div class="p-xl-3">
                      <div class="form-group">
                        <label class="control-label">利用者(前方一致)</label>
                        <input type="text" class="form-control" v-model="userDisplayName" maxlength="30"/>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row form-actions">
                  <div class="col-md-6 text-right">
                    <div class="p-xl-3">
                      <button type="submit" class="btn btn-success btn-circle" :disabled="isProcessing"
                              @click="search(1)">
                        <i class="fa fa-search"></i>
                      </button>
                    </div>
                  </div>
                  <div class="col-md-3 text-right">
                    <div class="p-xl-3">
                      <button type="submit" class="btn btn-block btn-secondary" :disabled="isProcessing"
                              @click="downloadCsv">
                        <span class="fa fa-download"></span>ダウンロード
                      </button>
                      <button v-if="isTeresaStandard" type="button" class="btn btn-block btn-secondary"
                        style="margin-top: 8px !important;" :disabled="isProcessing"
                        @click="downloadCsvTeresa">
                        <span class="fa fa-download"></span>テレッサ標準（利用者）出力
                      </button>
                    </div>
                  </div>
                  <div class="col-md-3 text-right">
                    <div class="data-actions">
                      <router-link to="/user_new" class="btn btn-rounded btn-outline-info">
                        <i class="fa fa-plus"></i>利用者を新規登録する
                      </router-link>
                    </div>
                  </div>
                </div>
                <div class="row" v-if="page">
                  <div class="col-sm-12">
                    <div class="table-responsive" v-if="page.data.length > 0">
                      <table id="myTable" class="table table-sm table-bordered dataTable">
                        <thead>
                        <tr>
                          <th :class="sortClass('id')" @click.prevent="sort('id')">利用者ID</th>
                          <th :class="sortClass('display_name')" @click.prevent="sort('display_name')">利用者名</th>
                          <th :class="sortClass('display_name_kana')" @click.prevent="sort('display_name_kana')">フリガナ
                          </th>
                          <th>登録日時</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="user in page.data" :key="user.id">
                          <td>
                            {{user.id}}
                          </td>
                          <td>
                            <router-link :to="`/user/${user.id}`">{{user.display_name}}</router-link>
                          </td>
                          <td>{{user.display_name_kana}}</td>
                          <td>{{user.created_at}}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-else>
                      <div class="alert alert-warning">
                        <p><i class="fa fa-info-circle p-r-10"/>該当するデータが見つかりませんでした。</p>

                        <p>検索のヒント</p>
                        <ul>
                          <li>
                            検索条件を変更してみてください。
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <pagination :page="page" v-if="page && page.data.length > 0" @change="changePage"/>
              </div>
            </div>
          </div>
          <!-- Column -->
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useStore } from 'vuex'
import api from '../../libs/Api'
import constants from '../../constants'
import Pagination from '../../components/Pagination.vue'
import SelectOffice from '../../components/SelectOffice'
import { useSortMixin } from '../../composables/useSortMixin'
import dayjs from 'dayjs'
import { useExecutor } from '@/libs/Executor'

const store = useStore()
const { exec } = useExecutor()

const { sortInfo, clearSort, sort, sortString, sortClass } = useSortMixin()

const profile = computed(() => store.state.session.profile)
const isTeresaStandard = computed(() =>
  (constants.FunctionMask.TeresaStandard & profile.value?.site?.function_mask) === constants.FunctionMask.TeresaStandard
)

// State
const page = ref(null)
const targetOfficeId = ref(null)
const userDisplayName = ref(null)
const userDisplayNameKana = ref(null)
const isProcessing = ref(false)
const errorOfficeId = ref(null)

const changePage = (pageNum) => {
  search(pageNum)
}

const search = async (pageNum = 1, clearSortFlag = false) => {
  if (clearSortFlag) {
    clearSort()
  }
  exec(async () => {
    isProcessing.value = true
    const res = await api.getUser(
      constants.QUERY_MODE_PAGING,
      pageNum,
      targetOfficeId.value,
      userDisplayName.value,
      userDisplayNameKana.value,
      '',
      sortString.value
    )
    console.info(res)
    page.value = res
  }).catch(err => {
    console.error('Error searching users:', err)
  }).finally(() => {
    isProcessing.value = false
  })
}

const downloadCsv = async () => {
  exec(async () => {
    isProcessing.value = true
    const query = createQuery()
    const res = await api.exportUser(query)
    saveCsvFile(res.data, 'user')
  }).catch(err => {
    console.error('Error downloading CSV:', err)
  }).finally(() => {
    isProcessing.value = false
  })
}

const downloadCsvTeresa = async () => {
  exec(async () => {
    isProcessing.value = true
    errorOfficeId.value = null

    // validate office id
    if (!targetOfficeId.value) {
      errorOfficeId.value = '事業部は必須です。'
      return
    }

    const query = createQuery()
    const res = await api.exportTeresaUser(query)
    saveCsvFile(res.data, 'user')
  }).catch((error) => {
    console.error('Error downloading CSV:', error)
  }).finally(() => {
    isProcessing.value = false
  })

  // try {
  //   isProcessing.value = true
  //   errorOfficeId.value = null
  //   const query = createQuery()
  //   const res = await api.exportTeresaUser(query)
  //   saveCsvFile(res.data, 'user')
  // } catch (error) {
  //   if (error.response && error.response.status === 422) {
  //     const data = error.response.data
  //     if (data instanceof Blob) {
  //       const text = await data.text()
  //       const json = JSON.parse(text)
  //       const errors = json.errors
  //       errorOfficeId.value = errors?.office_id ? 'は必須です。事業部' : null
  //     } else {
  //       const errors = data.errors
  //       errorOfficeId.value = errors?.office_id ? 'は必須です。事業部' : null
  //     }
  //   } else {
  //     console.error('Error downloading CSV:', error)
  //   }
  // } finally {
  //   isProcessing.value = false
  // }
}

const saveCsvFile = (res, filename) => {
  // CSVデータ取得 - BOM 付与
  const blob = new Blob([res], { type: 'text/csv' })
  filename += '_' + dayjs(Date.now()).format('YYYYMMDD_HHmmss') + '.csv'

  if (window.navigator.msSaveBlob) {
    // IE11 ( msSaveBlog が有効なら)
    window.navigator.msSaveBlob(blob, filename)
    window.navigator.msSaveOrOpenBlob(blob, filename)
  } else {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    link.click()
    window.URL.revokeObjectURL(url)
  }
}

const createQuery = () => {
  return {
    office_id: targetOfficeId.value,
    display_name: userDisplayName.value,
    display_name_kana: userDisplayNameKana.value,
    subDomain: window.location.hostname.split('.')[0]
  }
}

watch(() => sortInfo.sortVector, (newVal) => {
  if (!newVal) return
  search(1)
})
</script>

<style lang="scss" scoped>
.data-actions {
  text-align: right;
}

.table th {
  background-color: #99abb4 !important;
}
</style>
